import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';

const HomePage = () => {
  const principles = [
    { title: 'Build', desc: 'We create and develop operating businesses around substantive problems and practical opportunities.' },
    { title: 'Own', desc: 'We retain meaningful ownership and control where long-term alignment and strategic capability matter.' },
    { title: 'Steward', desc: 'We protect and develop companies, intellectual property, property and other assets with care and responsibility.' },
    { title: 'Compound', desc: 'We reinvest capital, knowledge and capability with a generational rather than short-term horizon.' },
  ];

  return (
    <Layout>
      <SEO
        title="Sandend House | The Knox Family Office"
        description="Sandend House is the first-generation private family office founded by Calum Knox, building and stewarding businesses, intellectual property and long-term assets."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Sandend House | The Knox Family Office",
          "url": "https://theknox.co.uk/",
          "isPartOf": { "@id": "https://theknox.co.uk/#website" },
          "about": { "@id": "https://theknox.co.uk/#organization" }
        }}
      />

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center bg-[var(--sh-ink)]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_aa62e94c-e559-4f19-a2b5-7166a5945181/artifacts/lnom93b4_IMG_0579.jpeg"
            alt="Sandend Bay, Scotland — village cottages along the shore at sunset"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--sh-ink)] via-[var(--sh-ink)]/60 to-[var(--sh-ink)]/40"></div>
        </div>
        <div className="relative section-container py-32 lg:py-40">
          <div className="max-w-3xl">
            <p className="eyebrow-light mb-6 fade-up" data-testid="hero-eyebrow">The Knox Family Office</p>
            <h1 className="font-serif text-white font-semibold tracking-[0.06em] uppercase leading-none mb-6 fade-up fade-up-delay-1" style={{ fontSize: 'clamp(2.75rem, 7vw, 5rem)' }} data-testid="hero-heading">
              Sandend House
            </h1>
            <p className="font-serif text-white/70 text-xl lg:text-2xl italic mb-8 fade-up fade-up-delay-2" data-testid="hero-tagline">
              Built through enterprise. Held for generations.
            </p>
            <div className="reading-width">
              <p className="text-white/55 text-base lg:text-lg leading-relaxed mb-10 fade-up fade-up-delay-3">
                Founded by Calum Knox, Sandend House is a first-generation family office created to build, own and steward businesses, intellectual property and long-term assets.
              </p>
              <p className="text-white/55 text-base lg:text-lg leading-relaxed mb-10 fade-up fade-up-delay-3">
                Built through enterprise rather than inherited from it, the House takes a patient approach to ownership, reinvestment and responsibility.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 fade-up fade-up-delay-4">
              <Link to="/founder" className="btn-primary-light" data-testid="hero-cta-founder">
                Meet the Founder <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link to="/portfolio" className="btn-ghost-light" data-testid="hero-cta-portfolio">
                Explore the Portfolio
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Purpose */}
      <section className="py-20 lg:py-28" data-testid="purpose-section">
        <div className="section-container">
          <p className="eyebrow mb-4">Our Purpose</p>
          <h2 className="font-serif text-3xl lg:text-4xl mb-8" style={{ maxWidth: '600px' }}>A permanent home for what we build.</h2>
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-4">
              Sandend House brings the ownership and stewardship of the Knox family's commercial interests into one long-term structure.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed mb-8">
              It is not a multi-family office and does not manage external client money. Its purpose is to develop the businesses and assets it owns, protect the intellectual property created within the group and reinvest with a horizon measured in decades.
            </p>
            <Link to="/the-house" className="btn-secondary" data-testid="purpose-cta">
              About Sandend House <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Four Principles */}
      <section className="py-20 lg:py-28 bg-white border-y border-[var(--sh-rule-light)]" data-testid="principles-section">
        <div className="section-container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
            {principles.map((p, i) => (
              <div key={i} data-testid={`principle-${p.title.toLowerCase()}`}>
                <h3 className="font-serif text-2xl mb-4 text-[var(--sh-ink)]">{p.title}</h3>
                <div className="w-10 h-px bg-[var(--sh-brass)] mb-4"></div>
                <p className="text-[var(--sh-grey)] text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-20 lg:py-28" data-testid="founder-section">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-4">Founder and Principal</p>
              <h2 className="font-serif text-3xl lg:text-4xl mb-8">Calum Knox</h2>
              <div className="reading-width">
                <p className="text-[var(--sh-charcoal)] leading-relaxed mb-4">
                  Calum Knox is a Scottish entrepreneur and the founder and CEO of The RLK Group.
                </p>
                <p className="text-[var(--sh-grey)] leading-relaxed mb-4">
                  His work spans artificial intelligence, autonomous systems, defence software, property technology and long-term asset ownership. He remains directly involved in group strategy, capital allocation, product direction, commercial partnerships and the creation of new ventures.
                </p>
                <p className="text-[var(--sh-grey)] leading-relaxed mb-8">
                  Sandend House provides the permanent ownership and stewardship structure around those interests.
                </p>
                <Link to="/founder" className="btn-secondary" data-testid="founder-cta">
                  Read Calum's Story <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] bg-[var(--sh-stone)] rounded overflow-hidden">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_aa62e94c-e559-4f19-a2b5-7166a5945181/artifacts/xnm4hccp_BFAA6454-E012-4E2A-A0D6-20883725C5C0.png"
                  alt="Calum Knox, Founder of Sandend House"
                  className="w-full h-full object-cover"
                  data-testid="founder-portrait"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section className="py-20 lg:py-28 bg-white border-y border-[var(--sh-rule-light)]" data-testid="portfolio-section">
        <div className="section-container">
          <p className="eyebrow mb-4">Selected Interests</p>
          <h2 className="font-serif text-3xl lg:text-4xl mb-4">Built, owned and operated with purpose.</h2>
          <p className="text-[var(--sh-grey)] mb-12 reading-width">
            The Sandend House portfolio is focused on businesses and assets where technical capability, operational experience and retained ownership can create durable value.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            {/* RLK Group */}
            <div className="border border-[var(--sh-rule)] rounded p-8 lg:p-10 hover:border-[var(--sh-grey)] transition-colors" data-testid="portfolio-rlk">
              <p className="eyebrow-brass mb-3">Advanced Technology</p>
              <h3 className="font-serif text-2xl mb-1">The RLK Group</h3>
              <p className="text-[var(--sh-grey)] text-xs mb-4">Founded by Calum Knox</p>
              <p className="text-[var(--sh-charcoal)] text-sm leading-relaxed mb-4">
                A UK technology group developing artificial intelligence, autonomous systems, defence software, simulation and operational analytics.
              </p>
              <p className="text-[var(--sh-grey)] text-xs mb-6">
                Including RLK Technologies and RLK Defence
              </p>
              <a href="https://therlkgroup.com" target="_blank" rel="noopener noreferrer" className="text-[var(--sh-ink)] text-sm font-medium inline-flex items-center gap-1 hover:text-[var(--sh-brass)] transition-colors">
                Visit The RLK Group <ArrowUpRight size={14} />
              </a>
            </div>

            {/* Letwise */}
            <div className="border border-[var(--sh-rule)] rounded p-8 lg:p-10 hover:border-[var(--sh-grey)] transition-colors" data-testid="portfolio-letwise">
              <p className="eyebrow-brass mb-3">Property Technology</p>
              <h3 className="font-serif text-2xl mb-1">Letwise</h3>
              <p className="text-[var(--sh-grey)] text-xs mb-4">Founded by Calum Knox</p>
              <p className="text-[var(--sh-charcoal)] text-sm leading-relaxed mb-6">
                A property technology and lettings platform designed to reduce management friction, improve transparency and automate routine property-management processes.
              </p>
              <a href="https://letwise.io" target="_blank" rel="noopener noreferrer" className="text-[var(--sh-ink)] text-sm font-medium inline-flex items-center gap-1 hover:text-[var(--sh-brass)] transition-colors">
                Visit Letwise <ArrowUpRight size={14} />
              </a>
            </div>
          </div>

          <Link to="/portfolio" className="btn-secondary" data-testid="portfolio-cta">
            View the Portfolio <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>

      {/* Intellectual Property */}
      <section className="py-20 lg:py-28" data-testid="ip-section">
        <div className="section-container">
          <p className="eyebrow mb-4">Intellectual Property</p>
          <h2 className="font-serif text-3xl lg:text-4xl mb-4">Protected by design.</h2>
          <p className="text-[var(--sh-grey)] mb-10 reading-width">
            Intellectual property is treated as a core asset rather than an afterthought. Across The RLK Group, the current portfolio includes filed patent applications, registered trademarks and a registered design protecting technologies in artificial intelligence, autonomy and operational systems.
          </p>
          <div className="flex flex-wrap gap-12 mb-10">
            <div>
              <div className="font-serif text-4xl text-[var(--sh-ink)]" data-testid="ip-patents">5</div>
              <p className="text-[var(--sh-grey)] text-sm mt-1">Filed patent applications</p>
            </div>
            <div>
              <div className="font-serif text-4xl text-[var(--sh-ink)]" data-testid="ip-trademarks">7</div>
              <p className="text-[var(--sh-grey)] text-sm mt-1">Registered trademarks</p>
            </div>
            <div>
              <div className="font-serif text-4xl text-[var(--sh-ink)]" data-testid="ip-designs">1</div>
              <p className="text-[var(--sh-grey)] text-sm mt-1">Registered design</p>
            </div>
          </div>
          <Link to="/intellectual-property" className="btn-secondary" data-testid="ip-cta">
            Explore the IP Portfolio <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>

      {/* Stewardship */}
      <section className="py-20 lg:py-28 bg-[var(--sh-stone)]/40 border-y border-[var(--sh-rule-light)]" data-testid="stewardship-section">
        <div className="section-container">
          <p className="eyebrow mb-4">Beyond Enterprise</p>
          <h2 className="font-serif text-3xl lg:text-4xl mb-4">Ownership carries responsibility.</h2>
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-4">
              Sandend House takes a long-term view of responsibility as well as ownership.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed mb-4">
              Its stewardship interests include animal welfare, responsible land use, education and practical community impact.
            </p>
            <div className="border border-[var(--sh-rule)] rounded p-6 bg-white mb-8">
              <h3 className="font-serif text-xl mb-2">The Grazing Trust</h3>
              <p className="text-[var(--sh-grey)] text-sm">A Scottish not-for-profit initiative focused on animal welfare, secure grazing and responsible land stewardship.</p>
            </div>
            <Link to="/stewardship" className="btn-secondary" data-testid="stewardship-cta">
              Our Stewardship <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Founder Statement */}
      <section className="relative py-20 lg:py-28 bg-[var(--sh-ink)] overflow-hidden" data-testid="statement-section">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_aa62e94c-e559-4f19-a2b5-7166a5945181/artifacts/clr0k538_IMG_0578.jpeg"
            alt="Sandend Bay at sunset"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--sh-ink)] via-[var(--sh-ink)]/80 to-[var(--sh-ink)]/60"></div>
        </div>
        <div className="relative section-container">
          <p className="eyebrow-light mb-6">A note from the founder</p>
          <div className="max-w-3xl">
            <blockquote className="font-serif text-white/90 text-xl lg:text-2xl leading-relaxed mb-4 italic">
              I did not establish Sandend House to make inherited wealth look more organised. I established it to build, protect and compound value through enterprise.
            </blockquote>
            <p className="text-white/55 leading-relaxed mb-4">
              Our starting point is not passive wealth management. It is the creation of useful businesses, defensible intellectual property and productive assets.
            </p>
            <p className="text-white/55 leading-relaxed mb-4">
              I believe ownership brings responsibility. Control should be retained where it matters, capital should be deployed patiently, and every business or asset should have a clear strategic purpose.
            </p>
            <p className="text-white/55 leading-relaxed mb-8">
              The objective is not to appear established. It is to become enduring.
            </p>
            <div className="border-t border-white/10 pt-6">
              <p className="text-white font-medium">Calum Knox</p>
              <p className="text-white/40 text-sm">Founder and Principal</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 lg:py-28" data-testid="contact-section">
        <div className="section-container text-center">
          <h2 className="font-serif text-3xl lg:text-4xl mb-4">Start a relevant conversation.</h2>
          <p className="text-[var(--sh-grey)] mb-8 max-w-lg mx-auto">
            Sandend House welcomes relevant corporate, strategic, property, intellectual-property, media and stewardship enquiries.
          </p>
          <Link to="/contact" className="btn-primary" data-testid="contact-cta">
            Contact Sandend House <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
